import React from "react";
import { Link, useLocation } from "react-router-dom";

import closeLightIcon from "../../assets/img/icons/closeLightIcon.svg";
import hamburgerMenuIcon from "../../assets/img/icons/hamburgerMenuIcon.svg";
import logo from "../../assets/img/icons/logo.svg";
import logoWithStar from "../../assets/img/icons/logo_w_star.svg";
import searchDarkIcon from "../../assets/img/icons/searchDarkIcon.svg";
import searchWhiteIcon from "../../assets/img/icons/searchWhiteIcon.svg";
import { useSiteData } from "../../contexts/SiteDataContext";
import { useLocale } from "../../i18n/LocaleContext";
import type { NavigationLink } from "../../types";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import MobileMenuModal from "../MobileMenuModal/MobileMenuModal";
import SearchInput from "../SearchInput/SearchInput";

interface HeaderProps {
  searchQuery?: string;
  setSearchQuery?: (value: string) => void;
  enableSearch?: boolean;
}

function Header({
  searchQuery = "",
  setSearchQuery = () => {},
  enableSearch = true,
}: HeaderProps) {
  const { siteData, setSiteData } = useSiteData();
  const { t } = useLocale();
  const location = useLocation();

  const toggleSearch = () => {
    const newIsSearchOpen = !siteData.isSearchOpen;
    setSiteData({ ...siteData, isSearchOpen: newIsSearchOpen });
    // Clear search query when closing search
    if (!newIsSearchOpen && searchQuery) {
      setSearchQuery("");
    }
  };

  const toggleMobileMenu = () => {
    setSiteData({ ...siteData, isMobileMenuOpen: !siteData.isMobileMenuOpen });
  };

  const navigationLinks: NavigationLink[] = [
    {
      href: "/rating",
      label: t("nav.rating"),
      isExternal: false,
      isMobileHighlight: true,
    },
    {
      href: "/protocol",
      label: t("nav.protocol"),
      isExternal: false,
    },
    {
      href: "#contacts",
      label: t("nav.contacts"),
      isExternal: false,
      onClick: (e) => {
        window.umami?.track("Click on Contact");
        e.preventDefault();
        setSiteData({
          ...siteData,
          isMobileMenuOpen: false,
          isOpenContactsModal: true,
        });
      },
    },
  ];

  const isCurrentPage = (href: string) => {
    return location.pathname === href;
  };

  return (
    <header className="w-full bg-dark text-white">
      <div className="container-lg py-7 hidden md:flex items-center gap-4 2xl:px-0 lg:px-4 md:px-4 px-4">
        <div className="flex justify-start shrink-0">
          <Link to="/" className="py-1" data-umami-event="Header Logo Click">
            <img
              src={logoWithStar}
              className="w-[16.77rem] h-auto"
              alt="Logo"
            />
          </Link>
        </div>
        <div className="flex items-center w-full lg:gap-4 gap-2 justify-end font-[900] text-center leading-[1.125rem] lg:text-[1.125rem] text-[1rem]">
          <ul className="flex items-center lg:gap-4 gap-2">
            {navigationLinks.map((link, index) => (
              <li key={index}>
                {link.href.startsWith("#") ? (
                  <a
                    href={link.href}
                    onClick={link.onClick}
                    className={`py-2 px-3 block leading-[1.125rem] ${
                      /* istanbul ignore next */
                      isCurrentPage(link.href) ? "text-green" : ""
                    }`}
                    data-umami-event="Header Link Click"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.href}
                    onClick={link.onClick}
                    className={`py-2 px-3 block leading-[1.125rem] ${
                      isCurrentPage(link.href) ? "text-green" : ""
                    }`}
                    data-umami-event="Header Link Click"
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          <LanguageSwitcher />
        </div>
      </div>

      <div className="py-2 flex px-2 items-center md:hidden">
        <div
          className="cursor-pointer w-12 h-10 items-center justify-center flex"
          onClick={toggleMobileMenu}
        >
          <img
            src={siteData.isMobileMenuOpen ? closeLightIcon : hamburgerMenuIcon}
            alt={siteData.isMobileMenuOpen ? "Close menu" : "Menu"}
            className="w-[1.375rem] h-[0.75rem]"
          />
        </div>
        <div className="flex-1 justify-center flex">
          <Link to="/" className="py-1" data-umami-event="Header Logo Click">
            <img src={logo} alt="Logo" className="logo-mobile" />
          </Link>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <LanguageSwitcher />
          {enableSearch && (
            <div
              className="cursor-pointer w-12 h-10 flex items-center justify-center"
              onClick={toggleSearch}
            >
              <img
                src={siteData.isSearchOpen ? closeLightIcon : searchWhiteIcon}
                alt={siteData.isSearchOpen ? "Close" : "Search"}
                className={`select-none ${
                  siteData.isSearchOpen ? "w-[0.79rem]" : "w-[1.125rem]"
                }`}
              />
            </div>
          )}
        </div>
      </div>

      {siteData.isSearchOpen && (
        <div className="bg-dark p-[0.625rem] md:hidden">
          <div className="relative w-full">
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-[0.1875rem] rounded"
            >
              <img
                src={searchDarkIcon}
                alt="Search"
                className="w-[1.125rem] h-[1.125rem]"
              />
            </button>
          </div>
        </div>
      )}

      <MobileMenuModal navigationLinks={navigationLinks} />
    </header>
  );
}

export default Header;
